import { SetUtils } from "../SetUtils";
export class CoreGraph {
  constructor() {
    this._next_id = 0;
    this._successors = /* @__PURE__ */ new Map();
    this._predecessors = /* @__PURE__ */ new Map();
    this._nodes_by_id = /* @__PURE__ */ new Map();
    this._nodesCount = 0;
    this._debugging = false;
    this._addedNodesDuringDebugging = /* @__PURE__ */ new Map();
    this._boundPredecessorIds = this.predecessorIds.bind(this);
    this._boundSuccessorIds = this.successorIds.bind(this);
  }
  startDebugging() {
    this._debugging = true;
  }
  stopDebugging() {
    this._debugging = false;
  }
  printDebug() {
    this._addedNodesDuringDebugging.forEach((node, nodeId) => {
      console.log(nodeId, node, node.graphPredecessors(), node.graphSuccessors());
    });
  }
  setScene(scene) {
    this._scene = scene;
  }
  scene() {
    return this._scene;
  }
  nextId() {
    this._next_id += 1;
    return this._next_id;
  }
  nodesFromIds(ids) {
    const nodes = [];
    for (let id of ids) {
      const node = this.nodeFromId(id);
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }
  nodeFromId(id) {
    return this._nodes_by_id.get(id);
  }
  hasNode(node) {
    return this._nodes_by_id.get(node.graphNodeId()) != null;
  }
  addNode(node) {
    this._nodes_by_id.set(node.graphNodeId(), node);
    this._nodesCount += 1;
    if (this._debugging) {
      this._addedNodesDuringDebugging.set(node.graphNodeId(), node);
    }
  }
  removeNode(node) {
    this._nodes_by_id.delete(node.graphNodeId());
    this._successors.delete(node.graphNodeId());
    this._predecessors.delete(node.graphNodeId());
    this._nodesCount -= 1;
    if (this._debugging) {
      this._addedNodesDuringDebugging.delete(node.graphNodeId());
    }
  }
  nodesCount() {
    return this._nodesCount;
  }
  connect(src, dest, check_if_graph_may_have_cycle = true) {
    const src_id = src.graphNodeId();
    const dest_id = dest.graphNodeId();
    if (this.hasNode(src) && this.hasNode(dest)) {
      if (check_if_graph_may_have_cycle) {
        const scene_loading = this._scene ? this._scene.loadingController.isLoading() : true;
        check_if_graph_may_have_cycle = !scene_loading;
      }
      let graph_would_have_cycle = false;
      if (check_if_graph_may_have_cycle) {
        graph_would_have_cycle = this._hasPredecessor(src_id, dest_id);
      }
      if (graph_would_have_cycle) {
        return false;
      } else {
        this._createConnection(src_id, dest_id);
        src.dirtyController.clearSuccessorsCacheWithPredecessors();
        return true;
      }
    } else {
      console.warn(`attempt to connect non existing node ${src_id} or ${dest_id}`);
      return false;
    }
  }
  disconnect(src, dest) {
    this._remove_connection(src.graphNodeId(), dest.graphNodeId());
    src.dirtyController.clearSuccessorsCacheWithPredecessors();
  }
  disconnectPredecessors(node) {
    const predecessors = this.predecessors(node);
    for (let predecessor of predecessors) {
      this.disconnect(predecessor, node);
    }
  }
  disconnectSuccessors(node) {
    const successors = this.successors(node);
    for (let successor of successors) {
      this.disconnect(node, successor);
    }
  }
  predecessorIds(id) {
    const map = this._predecessors.get(id);
    if (map) {
      const ids = [];
      map.forEach((bool, id2) => {
        ids.push(id2);
      });
      return ids;
    }
    return [];
  }
  predecessors(node) {
    const ids = this.predecessorIds(node.graphNodeId());
    return this.nodesFromIds(ids);
  }
  successorIds(id) {
    const map = this._successors.get(id);
    if (map) {
      const ids = [];
      map.forEach((bool, successorId) => {
        ids.push(successorId);
      });
      return ids;
    }
    return [];
  }
  successors(node) {
    const ids = this.successorIds(node.graphNodeId()) || [];
    return this.nodesFromIds(ids);
  }
  allPredecessorIds(node) {
    const method = this._boundPredecessorIds;
    const ids = /* @__PURE__ */ new Set();
    let nextIds = method(node.graphNodeId());
    while (nextIds.length > 0) {
      const nextNextIds = [];
      for (let nextId of nextIds) {
        ids.add(nextId);
        for (let nextNextId of method(nextId)) {
          nextNextIds.push(nextNextId);
        }
      }
      nextIds = nextNextIds;
    }
    return SetUtils.toArray(ids);
  }
  allSuccessorIds(node) {
    const method = this._boundSuccessorIds;
    const ids = [];
    let nextIds = method(node.graphNodeId());
    while (nextIds.length > 0) {
      const nextNextIds = [];
      for (let nextId of nextIds) {
        const nextNode = this.nodeFromId(nextId);
        if (nextNode && !nextNode.dirtyController.isForbiddenTriggerNodeId(node.graphNodeId())) {
          const currentIndex = ids.indexOf(nextId);
          if (currentIndex >= 0) {
            ids.splice(currentIndex, 1);
          }
          ids.push(nextId);
          for (let nextNextId of method(nextId)) {
            nextNextIds.push(nextNextId);
          }
        }
      }
      nextIds = nextNextIds;
    }
    return ids;
  }
  allPredecessors(node) {
    const ids = this.allPredecessorIds(node);
    return this.nodesFromIds(ids);
  }
  allSuccessors(node) {
    const ids = this.allSuccessorIds(node);
    return this.nodesFromIds(ids);
  }
  _createConnection(src_id, dest_id) {
    let node_successors = this._successors.get(src_id);
    if (!node_successors) {
      node_successors = /* @__PURE__ */ new Set();
      this._successors.set(src_id, node_successors);
    }
    if (node_successors.has(dest_id)) {
      return;
    }
    node_successors.add(dest_id);
    let node_predecessors = this._predecessors.get(dest_id);
    if (!node_predecessors) {
      node_predecessors = /* @__PURE__ */ new Set();
      this._predecessors.set(dest_id, node_predecessors);
    }
    node_predecessors.add(src_id);
  }
  _remove_connection(src_id, dest_id) {
    let node_successors = this._successors.get(src_id);
    if (node_successors) {
      node_successors.delete(dest_id);
      if (node_successors.size == 0) {
        this._successors.delete(src_id);
      }
    }
    let node_predecessors = this._predecessors.get(dest_id);
    if (node_predecessors) {
      node_predecessors.delete(src_id);
      if (node_predecessors.size == 0) {
        this._predecessors.delete(dest_id);
      }
    }
  }
  _hasPredecessor(src_id, dest_id) {
    const ids = this.predecessorIds(src_id);
    if (ids) {
      if (ids.includes(dest_id)) {
        return true;
      } else {
        for (let id of ids) {
          return this._hasPredecessor(id, dest_id);
        }
      }
    }
    return false;
  }
}
